import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Badge, Spinner, Alert, Collapse, Form
} from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../services/api';
import { formatDateTime } from '../utils/datetime';
import type { Email, CorpsComplet, Retour } from '../types';
import { etatAction, etatMail, STYLE_ETAT } from '../utils/etatAction';
import { trierActions, presentationAction, cibleAction, typeAction } from '../utils/actions';
import { Boite } from '../components/Boite';
import { RetourMail } from '../components/RetourMail';
import { libelleType, libelleSousType, etatLecture, raisonEchec } from '../utils/libelles';

/** Champs que l'API renvoie déjà (S59) sans qu'ils figurent dans `Email`. */
type Ligne = Email & { status?: string | null; attempts?: number | null; error_message?: string | null };

type Filtre = 'tous' | 'echecs' | 'bloques' | 'transferts' | 'signales';

const estEnEchec = (e: Ligne) =>
  etatLecture(e) === 'echec' || (e.actions || []).some(a => etatAction(a) === 'echec');
const estBloque = (e: Ligne) => etatLecture(e) === 'bloque' || etatLecture(e) === 'echec';
const aUnTransfert = (e: Ligne) => (e.actions || []).some(a => typeAction(a) === 'FORWARD_EMAIL');
const estSignale = (e: Ligne) => e.retour?.verdict === 'erreur';
const ciblesDe = (e: Ligne, type: string) =>
  (e.actions || []).filter(a => typeAction(a) === type).map(cibleAction).filter(Boolean);

interface LayoutContext {
  refreshKey: number;
  onLogout: () => void;
  setLastUpdate: (d: Date) => void;
}

/**
 * Le corps stocké est `email.body[:500]` BRUT, sans conversion HTML -> texte.
 * Pour un mail HTML d'Outlook ces 500 caractères sont l'en-tête : <head>,
 * @font-face, déclarations de police. 28 des 207 mails du tenant sont dans ce
 * cas (constat du 09.09). On nettoie ce qu'on peut, et quand il ne reste que du
 * balisage on le dit au lieu d'afficher du bruit.
 *
 * Correctif de fond côté serveur (convertir avant de tronquer) : backlog Lot 3a.
 */
const nettoyerCorps = (brut?: string | null): string => {
  if (!brut) return '';
  let t = brut
    .replace(/<head[\s\S]*?<\/head>/gi, ' ')
    .replace(/<style[\s\S]*?(<\/style>|$)/gi, ' ')
    .replace(/<script[\s\S]*?(<\/script>|$)/gi, ' ')
    .replace(/<!--[\s\S]*?(-->|$)/g, ' ');
  // Un aperçu tronqué peut ouvrir <head> sans jamais le refermer.
  const ouvertureHead = t.search(/<head\b/i);
  if (ouvertureHead !== -1) t = t.slice(0, ouvertureHead);
  t = t
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li)>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  return t.replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
};

/** Ne reste-t-il que du balisage, sans rien de métier ? */
const corpsInexploitable = (nettoye: string): boolean =>
  nettoye.length < 20 || /@font-face|mso-|font-family\s*:/i.test(nettoye);

export const EmailsPage: React.FC = () => {
  const { refreshKey, onLogout, setLastUpdate } = useOutletContext<LayoutContext>();
  const [emails, setEmails] = useState<Ligne[]>([]);
  const [filtre, setFiltre] = useState<Filtre>('tous');
  const [boite, setBoite] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEmail, setExpandedEmail] = useState<number | null>(null);
  // S67 — la profondeur était figée à 50 : le client ne pouvait pas voir sa
  // propre campagne de test (97 emails) en entier.
  const [limit, setLimit] = useState<number>(50);
  // S116 : le mail complet, lu à la demande dans la boîte, jamais stocké.
  const [corps, setCorps] = useState<Record<string, CorpsComplet | 'chargement' | string>>({});

  const chargerCorps = async (messageId: string) => {
    setCorps(c => ({ ...c, [messageId]: 'chargement' }));
    try {
      const r = await adminApi.getCorpsComplet(messageId);
      setCorps(c => ({ ...c, [messageId]: r }));
    } catch (err: any) {
      setCorps(c => ({ ...c, [messageId]: err.response?.data?.detail || 'Lecture du mail impossible' }));
    }
  };

  const majRetour = (messageId: string, r: Retour | null) =>
    setEmails(es => es.map(e => (e.message_id === messageId ? { ...e, retour: r } : e)));

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const data = await adminApi.getRecentEmails(limit);
      setEmails(data.emails);
      setLastUpdate(new Date());
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Session expirée, reconnectez-vous.');
        setTimeout(() => onLogout(), 2000);
      } else {
        setError(err.message || 'Chargement des mails impossible');
      }
    } finally {
      setLoading(false);
    }
  }, [limit, onLogout, setLastUpdate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (refreshKey > 0) fetchData();
  }, [refreshKey, fetchData]);


  const getTypeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      'client': 'success', 'supplier': 'primary', 'web': 'info',
      'mailchimp': 'warning', 'unknown': 'secondary'
    };
    return colors[type] || 'secondary';
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted fw-semibold">Chargement des mails...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <Alert.Heading>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>Erreur
        </Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  const boites = Array.from(new Set(emails.map(e => e.mailbox))).sort();
  const compte = {
    tous: emails.length,
    echecs: emails.filter(estEnEchec).length,
    bloques: emails.filter(estBloque).length,
    transferts: emails.filter(aUnTransfert).length,
    signales: emails.filter(estSignale).length,
  };
  const visibles = emails.filter(e =>
    (boite === null || e.mailbox === boite) &&
    (filtre === 'tous' || (filtre === 'echecs' && estEnEchec(e)) ||
      (filtre === 'bloques' && estBloque(e)) || (filtre === 'transferts' && aUnTransfert(e)) ||
      (filtre === 'signales' && estSignale(e))));
  const pastille = (actif: boolean) => ({
    fontSize: '0.78rem', padding: '2px 10px', borderRadius: '999px', cursor: 'pointer',
    border: `1px solid ${actif ? '#212529' : '#dee2e6'}`,
    background: actif ? '#212529' : '#fff', color: actif ? '#fff' : '#333',
  });

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-white border-0 pt-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <h5 className="mb-0 fw-semibold fs-6">
            <i className="bi bi-envelope-open me-2"></i>
            Mails récents ({visibles.length}{visibles.length !== emails.length ? ` sur ${emails.length}` : ''})
          </h5>
          <div className="d-flex align-items-center gap-2">
            {/* S67 — afficher la profondeur choisie ET si la vue est tronquée :
                une liste coupée ne doit jamais se lire comme un historique complet. */}
            {emails.length === limit && (
              <small className="text-muted">
                affichage limité aux {limit} plus récents
              </small>
            )}
            <Form.Label htmlFor="email-limit" className="mb-0 small text-muted">
              Afficher
            </Form.Label>
            <Form.Select
              id="email-limit"
              size="sm"
              style={{ width: 'auto' }}
              value={limit}
              onChange={(e) => {
                setLoading(true);
                setLimit(Number(e.target.value));
              }}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </Form.Select>
          </div>
        </div>
      </Card.Header>
      {/* Filtres rapides, côté navigateur, sur les mails déjà chargés. */}
      <div className="d-flex flex-wrap align-items-center gap-2 px-3 pb-2">
        {([['tous', 'Tous'], ['echecs', 'Échecs'], ['bloques', 'Bloqués'], ['transferts', 'Avec transfert'], ['signales', 'Signalés']] as [Filtre, string][]).map(([f, l]) => (
          <span key={f} role="button" style={pastille(filtre === f)} onClick={() => setFiltre(f)}>
            {l} <span style={{ opacity: 0.7 }}>{compte[f]}</span>
          </span>
        ))}
        <span className="text-muted mx-1">|</span>
        {boites.map(b => (
          <span key={b} role="button" style={pastille(boite === b)} onClick={() => setBoite(boite === b ? null : b)}>
            {b}
          </span>
        ))}
      </div>
      <Card.Body className="p-0">
        <div className="table-responsive" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {/* Largeurs fixes : ouvrir un mail ne doit pas redimensionner les colonnes. */}
          <Table hover className="mb-0" style={{ tableLayout: 'fixed', minWidth: '1100px' }}>
            <colgroup>
              <col style={{ width: '32px' }} />
              <col style={{ width: '90px' }} />
              <col style={{ width: '125px' }} />
              <col style={{ width: '190px' }} />
              <col />
              <col style={{ width: '130px' }} />
              <col style={{ width: '32%' }} />
              <col style={{ width: '120px' }} />
            </colgroup>
            <thead className="table-light sticky-top">
              <tr>
                <th className="border-0"></th>
                <th className="border-0">Boîte</th>
                <th className="border-0">Reçu</th>
                <th className="border-0">De</th>
                <th className="border-0">Sujet</th>
                <th className="border-0">Type</th>
                <th className="border-0">Actions</th>
                <th className="border-0 text-center">Statut</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((email) => (
                <React.Fragment key={email.id}>
                  <tr
                    onClick={() => setExpandedEmail(expandedEmail === email.id ? null : email.id)}
                    style={{ cursor: 'pointer' }}
                    className={expandedEmail === email.id ? 'table-active' : ''}
                  >
                    <td>
                      <i className={`bi ${expandedEmail === email.id ? 'bi-chevron-down' : 'bi-chevron-right'} text-muted`}></i>
                    </td>
                    <td>
                      <Boite nom={email.mailbox} />
                    </td>
                    <td>
                      <div className="text-muted" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        {formatDateTime(email.received_at)}
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="fw-semibold small text-truncate">{email.sender_name || email.sender_email || (etatLecture(email) === 'lu' ? 'Inconnu' : 'Pas encore lu')}</div>
                        <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>
                          {email.sender_email}
                        </div>
                      </div>
                    </td>
                    <td>
                      {etatLecture(email) === 'lu' ? (
                        <div className="text-truncate" style={{ fontSize: '0.82rem' }} title={email.subject}>
                          {email.subject}
                        </div>
                      ) : (
                        <div className="text-truncate text-muted fst-italic" style={{ fontSize: '0.82rem' }} title={email.error_message || ''}>
                          {etatLecture(email) === 'en_cours' ? 'Lecture en cours' : raisonEchec(email.error_message)}
                        </div>
                      )}
                    </td>
                    <td>
                      {email.classification ? (
                        <>
                          <Badge bg={getTypeColor(email.classification.email_type || 'unknown')}>
                            {libelleType(email.classification.email_type)}
                          </Badge>
                          {email.classification.email_subtype && (
                            <div className="text-muted text-truncate" style={{ fontSize: '0.72rem' }}>
                              {libelleSousType(email.classification.email_subtype)}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-muted small">--</span>
                      )}
                    </td>
                    {/* Actions dans l'ordre d'exécution, reliées par une flèche. L'état
                        « prévu » (lecture seule) n'est pas répété ici : le bandeau et le
                        statut du mail le disent déjà. Seul un échec se signale par chip. */}
                    <td>
                      {email.actions && email.actions.length > 0 ? (
                        <div className="d-flex flex-wrap align-items-center" style={{ gap: '2px 4px' }}>
                          {trierActions(email.actions).map((action, idx) => {
                            const p = presentationAction(action);
                            const etat = etatAction(action);
                            const cible = cibleAction(action);
                            return (
                              <span key={idx} className="d-inline-flex align-items-center" style={{ gap: '4px', minWidth: 0 }}>
                                {idx > 0 && <i className="bi bi-chevron-right text-muted" style={{ fontSize: '0.6rem' }}></i>}
                                <span
                                  className="d-inline-flex align-items-center text-truncate"
                                  style={{
                                    fontSize: '0.72rem',
                                    padding: '1px 6px',
                                    borderRadius: '4px',
                                    maxWidth: '180px',
                                    color: etat === 'echec' ? '#dc3545' : '#333',
                                    background: etat === 'echec' ? '#fdecee' : '#f3f5f7',
                                    border: `1px solid ${etat === 'echec' ? '#dc3545' : '#dde2e6'}`,
                                    textDecoration: etat === 'annule' ? 'line-through' : undefined,
                                  }}
                                  title={`${p.libelle}${cible ? ' : ' + cible : ''} · ${STYLE_ETAT[etat].libelle}`}
                                >
                                  <i className={`bi ${p.icone} me-1`} style={{ color: p.couleur, fontSize: '0.7rem' }}></i>
                                  <span className="text-truncate">{cible || p.libelle}</span>
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-muted small">--</span>
                      )}
                    </td>
                    <td className="text-center text-nowrap">
                      {email.retour && (
                        <i
                          className={`bi ${email.retour.verdict === 'juste' ? 'bi-check-circle-fill' : 'bi-flag-fill'} me-1`}
                          style={{ color: email.retour.verdict === 'juste' ? '#198754' : '#b98900', fontSize: '0.8rem' }}
                          title={email.retour.verdict === 'juste' ? 'Marqué juste' : 'Erreur signalée'}
                        ></i>
                      )}
                      {etatLecture(email) === 'en_cours' ? (
                        <Badge bg="light" text="dark" className="px-2 border fw-normal">En cours</Badge>
                      ) : etatLecture(email) === 'introuvable' ? (
                        <span className="d-inline-block px-2 rounded small text-muted"
                              style={{ background: '#f3f5f7', border: '1px solid #dde2e6', lineHeight: 1.3 }}
                              title={email.error_message || ''}>
                          <i className="bi bi-trash3 me-1"></i>Supprimé
                        </span>
                      ) : etatLecture(email) !== 'lu' ? (
                        <span
                          className="d-inline-block px-2 rounded small"
                          style={{ background: '#fff3cd', color: '#7a5a00', border: '1px solid #f0d27a', lineHeight: 1.3 }}
                          title={email.error_message || ''}
                        >
                          <i className="bi bi-exclamation-triangle me-1"></i>Bloqué
                          {email.attempts && email.attempts > 1 && (
                            <div style={{ fontSize: '0.68rem' }}>{email.attempts} essais</div>
                          )}
                        </span>
                      ) : email.user_corrected_category ? (
                        <Badge bg="warning" className="px-2">
                          <i className="bi bi-pencil-square me-1"></i>
                          Corrigé
                        </Badge>
                      ) : email.actions && email.actions.length > 0 ? (
                        {
                          fait: (
                            <Badge bg="success" className="px-2">
                              <i className="bi bi-check-circle me-1"></i>
                              OK
                            </Badge>
                          ),
                          prevu: (
                            <Badge bg="light" text="dark" className="px-2 border fw-normal" title="Lecture seule : ces actions sont celles que l'agent ferait, aucune n'a été exécutée.">
                              À blanc
                            </Badge>
                          ),
                          annule: (
                            <Badge bg="secondary" className="px-2" title="Rangement annulé par l'invariant S91 : le mail reste visible en boîte.">
                              <i className="bi bi-slash-circle me-1"></i>
                              Visible
                            </Badge>
                          ),
                          echec: (
                            <Badge bg="danger" className="px-2">
                              <i className="bi bi-x-circle me-1"></i>
                              Échec
                            </Badge>
                          ),
                        }[etatMail(email.actions)]
                      ) : (
                        <Badge bg="secondary" className="px-2">--</Badge>
                      )}
                    </td>
                  </tr>
                  {/* Expanded Action Pipeline */}
                  <tr>
                    <td colSpan={8} className="p-0 border-0">
                      <Collapse in={expandedEmail === email.id}>
                        <div>
                          <div className="bg-light p-3 mx-2 mb-2 rounded">
                            {/* Ce que le MODÈLE a compris, et rien de plus. Ce n'est PAS
                                l'explication du routage : les règles déterministes
                                s'appliquent après lui et peuvent réécrire son verdict ou
                                retirer un transfert (mémoire de fil). Sur la commande
                                Biomedon du 07.09, le modèle dit « explicit order » alors que
                                la seule action est un tag, parce que la même commande était
                                déjà partie 3 h plus tôt. Le titre doit donc dire « compris »,
                                jamais « pourquoi ».

                                Le badge de confiance a été retiré volontairement : sur 210
                                mails du tenant, 198 sont à 0,95 ou 0,98. C'est une constante
                                déguisée en mesure, et l'afficher revient à revendiquer une
                                certitude sur des verdicts parfois faux. */}
                            {email.classification && (email.classification.reasoning ||
                              email.classification.client_name || email.classification.supplier_name) && (
                              <div className="mb-3 p-2 bg-white rounded border border-primary-subtle" style={{ fontSize: '0.8rem' }}>
                                <div className="fw-semibold text-muted mb-1">
                                  <i className="bi bi-lightbulb me-1"></i>Ce que l'IA a compris du mail
                                </div>
                                <div className="text-muted fst-italic mb-2" style={{ fontSize: '0.72rem' }}>
                                  Lecture du modèle. Les règles de routage s'appliquent ensuite
                                  et peuvent la corriger : ce bloc n'explique pas à lui seul les
                                  actions ci-dessous.
                                </div>

                                {/* Les champs extraits, ce sont EUX qui pilotent la
                                    recherche du commercial (nom, puis NPA, puis localité). */}
                                {(email.classification.client_name || email.classification.supplier_name ||
                                  email.classification.client_postal_code || email.classification.client_city) && (
                                  <div className="mb-2 d-flex flex-wrap gap-2">
                                    {email.classification.client_name && (
                                      <Badge bg="light" text="dark" className="border fw-normal">
                                        Client&nbsp;: {email.classification.client_name}
                                      </Badge>
                                    )}
                                    {email.classification.supplier_name && (
                                      <Badge bg="light" text="dark" className="border fw-normal">
                                        Fournisseur&nbsp;: {email.classification.supplier_name}
                                      </Badge>
                                    )}
                                    {email.classification.client_postal_code && (
                                      <Badge bg="light" text="dark" className="border fw-normal">
                                        NPA&nbsp;{email.classification.client_postal_code}
                                      </Badge>
                                    )}
                                    {email.classification.client_city && (
                                      <Badge bg="light" text="dark" className="border fw-normal">
                                        {email.classification.client_city}
                                      </Badge>
                                    )}
                                  </div>
                                )}

                                {email.classification.reasoning && (
                                  <div style={{ whiteSpace: 'pre-wrap', color: '#333', maxHeight: '220px', overflowY: 'auto' }}>
                                    {email.classification.reasoning}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Correction notice */}
                            {email.user_corrected_category && (
                              <div className="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                <span>
                                  <strong>Correction manuelle :</strong> tag changé
                                  {email.applied_categories && email.applied_categories.length > 0 && (
                                    <> de <Badge bg="secondary">{email.applied_categories[0]}</Badge></>
                                  )}
                                  {' '}en <Badge bg="warning">{email.user_corrected_category}</Badge>
                                  {email.process_count && email.process_count > 1 && (
                                    <span className="text-muted ms-2">(traité {email.process_count} fois)</span>
                                  )}
                                </span>
                              </div>
                            )}

                            {/* Déroulé sur une ligne, dans l'ordre d'exécution. Pas d'icône
                                d'état par étape : le titre dit une fois si rien n'a été
                                exécuté. Un échec reste signalé sur son étape, avec son message. */}
                            {email.actions && email.actions.length > 0 ? (
                              <div>
                                <div className="fw-semibold text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                                  {etatMail(email.actions) === 'prevu'
                                    ? 'Déroulé prévu (lecture seule, rien n\'a été exécuté)'
                                    : 'Déroulé'}
                                </div>
                                <div className="d-flex flex-wrap align-items-stretch" style={{ gap: '6px' }}>
                                  {trierActions(email.actions).map((action, idx) => {
                                    const p = presentationAction(action);
                                    const etat = etatAction(action);
                                    return (
                                      <React.Fragment key={idx}>
                                        {idx > 0 && (
                                          <div className="d-flex align-items-center text-muted">
                                            <i className="bi bi-arrow-right"></i>
                                          </div>
                                        )}
                                        <div
                                          className="bg-white rounded px-2 py-1"
                                          style={{
                                            border: `1px solid ${etat === 'echec' ? '#dc3545' : '#dde2e6'}`,
                                            borderTop: `3px solid ${etat === 'echec' ? '#dc3545' : p.couleur}`,
                                            minWidth: '140px',
                                            maxWidth: '320px',
                                            fontSize: '0.8rem',
                                          }}
                                        >
                                          <div className="fw-semibold" style={{ color: p.couleur }}>
                                            <i className={`bi ${p.icone} me-1`}></i>{p.libelle}
                                          </div>
                                          <div className="text-break" style={{ color: '#333' }}>
                                            {cibleAction(action) || action.details || ''}
                                          </div>
                                          {etat === 'echec' && action.error_message && (
                                            <div className="text-danger small mt-1">
                                              <i className="bi bi-x-circle me-1"></i>{action.error_message}
                                            </div>
                                          )}
                                          {etat === 'annule' && (
                                            <div className="text-muted small mt-1">{STYLE_ETAT.annule.libelle}</div>
                                          )}
                                        </div>
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <div className="text-muted text-center py-2 small">
                                Aucune action enregistrée pour ce mail
                              </div>
                            )}

                            {etatLecture(email) === 'lu' && (
                              <RetourMail
                                key={`${email.message_id}-${email.retour?.verdict || 'aucun'}`}
                                messageId={email.message_id}
                                retour={email.retour}
                                typeAgent={email.classification?.email_type}
                                sousTypeAgent={email.classification?.email_subtype}
                                destinatairesAgent={ciblesDe(email, 'FORWARD_EMAIL')}
                                dossierAgent={ciblesDe(email, 'MOVE_EMAIL')[0]}
                                adressesConnues={Array.from(new Set(emails.flatMap(e => ciblesDe(e, 'FORWARD_EMAIL')))).sort()}
                                onChange={r => majRetour(email.message_id, r)}
                              />
                            )}

                            {/* Le corps du mail EN DERNIER. Il occupait la première place
                                et poussait la chronologie sous la ligne de flottaison :
                                le client en a conclu que « certains mails n'ont pas le
                                déroulé » (retour du 09.09). */}
                            <div className="mt-3 p-2 bg-white rounded border" style={{ fontSize: '0.8rem' }}>
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="fw-semibold text-muted">
                                  <i className="bi bi-envelope-paper me-1"></i>Corps du mail
                                  <span className="fw-normal ms-1">
                                    {typeof corps[email.message_id] === 'object'
                                      ? '(complet, lu dans la boîte à l\'instant)'
                                      : '(aperçu, 500 premiers caractères)'}
                                  </span>
                                </div>
                                {typeof corps[email.message_id] !== 'object' && (
                                  <span role="button" className="small text-primary" onClick={() => chargerCorps(email.message_id)}>
                                    {corps[email.message_id] === 'chargement'
                                      ? <><Spinner animation="border" size="sm" className="me-1" />Lecture...</>
                                      : <><i className="bi bi-arrows-angle-expand me-1"></i>Voir le mail complet</>}
                                  </span>
                                )}
                              </div>
                              {typeof corps[email.message_id] === 'object' ? (
                                <>
                                  <div style={{ whiteSpace: 'pre-wrap', color: '#333', maxHeight: '480px', overflowY: 'auto' }}>
                                    {(corps[email.message_id] as CorpsComplet).texte || 'Mail sans texte.'}
                                  </div>
                                  {(corps[email.message_id] as CorpsComplet).pieces_jointes.length > 0 && (
                                    <div className="mt-2 text-muted">
                                      <i className="bi bi-paperclip me-1"></i>
                                      {(corps[email.message_id] as CorpsComplet).pieces_jointes.map(p => p.nom).join(', ')}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  {typeof corps[email.message_id] === 'string' && corps[email.message_id] !== 'chargement' && (
                                    <div className="text-danger mb-1">{corps[email.message_id] as string}</div>
                                  )}
                                  {!email.body_preview || corpsInexploitable(nettoyerCorps(email.body_preview)) ? (
                                    <div className="text-muted fst-italic">
                                      Aperçu indisponible : l'extrait stocké ne contient que l'en-tête
                                      HTML du message, sans texte métier.
                                    </div>
                                  ) : (
                                    <div style={{ whiteSpace: 'pre-wrap', color: '#333', maxHeight: '200px', overflowY: 'auto' }}>
                                      {nettoyerCorps(email.body_preview)}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            {/* Tags applied */}
                            {email.applied_categories && email.applied_categories.length > 0 && (
                              <div className="mt-2 small text-muted">
                                <i className="bi bi-tags me-1"></i>
                                Tags appliqués : {email.applied_categories.map((cat, i) => (
                                  <Badge key={i} bg="outline-primary" className="border border-primary text-primary me-1">{cat}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};
